import { useAuthStore } from '../stores/authStore';

/**
 * Get the organization timezone from the auth store.
 * Falls back to browser's local timezone if not available.
 */
function getOrgTimezone() {
  try {
    const user = useAuthStore.getState().user;
    const tz = user?.organization?.timezone;
    if (tz) {
      // Validate the timezone string
      Intl.DateTimeFormat(undefined, { timeZone: tz });
      return tz;
    }
  } catch {
    // Invalid timezone string, fall back
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Format a date/datetime string using the organization's timezone.
 * This replaces direct use of toLocaleString() which uses the browser's timezone.
 *
 * @param {string|Date} date - ISO date string or Date object
 * @param {object} options - Additional Intl.DateTimeFormat options
 * @returns {string} Formatted date string in org timezone
 */
export function formatDateTime(date, options = {}) {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  const tz = getOrgTimezone();
  return d.toLocaleString(undefined, {
    timeZone: tz,
    ...options,
  });
}

/**
 * Format just the date portion using the organization's timezone.
 */
export function formatDate(date, options = {}) {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  const tz = getOrgTimezone();
  return d.toLocaleDateString(undefined, {
    timeZone: tz,
    ...options,
  });
}

/**
 * Format just the time portion using the organization's timezone.
 */
export function formatTime(date, options = {}) {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  const tz = getOrgTimezone();
  return d.toLocaleTimeString(undefined, {
    timeZone: tz,
    ...options,
  });
}
